import { ChangeDetectionStrategy, Component, Inject, Input, ViewChild } from '@angular/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ObservableWorkState } from '@vendasta/rx-utils/work-state';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import {
  AskQuestionServiceInterface,
  AskQuestionServiceInterfaceToken,
} from '../ask-question-dialog/ask-question-dialog-interface';
import { ProjectCommentsComponent } from '../project-comments/project-comments.component';
import { ProjectTrackerServiceInterface, ProjectTrackerServiceInterfaceToken } from '../project-tracker-interface';
import {
  AddCommentEvent,
  ExtendedCommentInterface,
  ExtendedConversationInterface,
  ProjectTrackerInfo,
  ProjectTrackerUserDetails,
} from '../shared/interfaces';
import { SidenavService } from '../shared/services/sidenav.service';
import { ProjectTrackerUtils } from '../shared/utils';

@Component({
  selector: 'project-tracker-comments',
  templateUrl: './project-tracker-comments.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectTrackerCommentsComponent {
  @Input()
  set projectTrackerInfo(value: ProjectTrackerInfo) {
    this.project = value;
    this.refreshProjectTrackerInfo();
  }

  @ViewChild('projectComments') projectComments: ProjectCommentsComponent;

  private project: ProjectTrackerInfo;
  conversation$$: BehaviorSubject<ExtendedCommentInterface[]> = new BehaviorSubject<ExtendedCommentInterface[]>([]);
  isLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private workableState: ObservableWorkState<any> = new ObservableWorkState<any>();
  createCommentLoading$: Observable<boolean> = this.workableState.isLoading$;
  activeUser$: Observable<ProjectTrackerUserDetails> = this.projectTrackerService.activeUser$;

  constructor(
    private sidenavService: SidenavService,
    @Inject(AskQuestionServiceInterfaceToken) private askQuestionService: AskQuestionServiceInterface,
    private readonly snackbarService: SnackbarService,
    @Inject(ProjectTrackerServiceInterfaceToken) private projectTrackerService: ProjectTrackerServiceInterface,
  ) {}

  private static getConversationFromInbox(project: ProjectTrackerInfo): Observable<ExtendedCommentInterface[]> {
    return of(
      project.projectConversations?.map((conversation) => {
        const result: ExtendedCommentInterface = ProjectTrackerUtils.mapInternalConversationToInbox(conversation);
        const subtaskId = conversation.source?.split('/')?.slice(-1)?.pop(); // source = externalMessageId
        const subtask = project.subtasks?.find(
          (task) =>
            task.subtaskId?.split('/')?.slice(-1)?.pop() === subtaskId || task?.['identity']?.['taskId'] === subtaskId,
        );
        result.taskTitle = subtask?.title;
        return result;
      }),
    );
  }

  private refreshProjectTrackerInfo(): void {
    this.isLoading$$.next(true);
    ProjectTrackerCommentsComponent.getConversationFromInbox(this.project)
      .pipe(
        take(1),
        finalize(() => this.isLoading$$.next(false)),
      )
      .subscribe((result) => this.conversation$$.next(result));
  }

  private appendComment(newComment: ExtendedConversationInterface): void {
    const conversation = this.conversation$$.getValue() || [];
    conversation.unshift(ProjectTrackerUtils.mapInternalConversationToInbox(newComment));
    this.conversation$$.next(conversation);

    if (this.project?.projectConversations) {
      this.project.projectConversations.unshift(newComment);
    }
  }

  handleSubmitClick(event: AddCommentEvent): void {
    if (this.project?.projectIdentity) {
      const work$ = this.askQuestionService.askQuestion(event?.message, this.project.projectIdentity).pipe(take(1));
      this.workableState.startWork(work$);
      combineLatest([this.workableState.isSuccess$, this.activeUser$])
        .pipe(take(1))
        .subscribe(([isSuccess, activeUser]) => {
          if (isSuccess) {
            this.snackbarService.openSuccessSnack('COMMENT.NEW_COMMENT.SUBMIT_SUCCESS');

            const newComment: ExtendedConversationInterface = {
              sendFrom: activeUser?.userInfo?.firstName,
              message: event?.message,
              created: new Date(),
              isPublic: event?.isPublic,
              senderImageUrl: activeUser.profilePictureUrl,
            };
            this.appendComment(newComment);
            this.projectComments.clearCommentInput();
          } else {
            this.snackbarService.openErrorSnack('COMMENT.NEW_COMMENT.SUBMIT_ERROR');
          }
        });
    }
  }

  handleCloseClick(): void {
    this.sidenavService.close();
  }
}
